import { Button, Card, CardHeader, MonoLabel, StatusPill } from '@/design-system';
import type { InvoiceBatchOutcome } from '@/entities/invoice';

import { billingCopy } from '../copy';

export interface BatchResultCardProps {
    outcome: InvoiceBatchOutcome;
    /** Hoe de facturen heetten toen ze verstuurd werden (`concept 4F2K9Q`), per uuid. */
    labels: ReadonlyMap<string, string>;
    onRetry: (invoiceUuids: string[]) => void;
    onDismiss: () => void;
    retrying?: boolean;
}

/**
 * De uitkomst van "facturen versturen".
 *
 * Eén actie, maar nooit één uitkomst: een batch die halverwege strandt is niet
 * "gelukt" en niet "mislukt". Per factuur die bleef liggen staat hier waarom,
 * en alleen wat echt mislukte (niet: geen toestemming, niet gevonden) kan
 * opnieuw geprobeerd worden.
 */
export function BatchResultCard({
    outcome,
    labels,
    onRetry,
    onDismiss,
    retrying = false,
}: BatchResultCardProps) {
    const notSent = outcome.lines.filter((line) => line.result !== 'sent');
    const retryable = notSent.filter((line) => line.result === 'failed');
    const complete = notSent.length === 0;

    return (
        <Card clip as="section" aria-labelledby="batch-result-title">
            <div role="status" aria-live="polite">
                <CardHeader
                    title={
                        <span id="batch-result-title">
                            {complete
                                ? billingCopy.batchTitleComplete(outcome.sent)
                                : billingCopy.batchTitlePartial(outcome.sent, outcome.requested)}
                        </span>
                    }
                    description={
                        complete
                            ? billingCopy.batchComplete
                            : billingCopy.batchPartial(notSent.length)
                    }
                    action={
                        <Button variant="ghost" size="xs" onClick={onDismiss}>
                            {billingCopy.close}
                        </Button>
                    }
                />
            </div>

            {notSent.length > 0 ? (
                <ul className="m-0 list-none p-0">
                    {notSent.map((line) => (
                        <li
                            key={line.invoiceUuid}
                            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--border-subtle-2)] px-4 py-[11px]"
                        >
                            <MonoLabel size={11} variant="value" tone="muted">
                                {line.number ?? labels.get(line.invoiceUuid) ?? line.invoiceUuid}
                            </MonoLabel>
                            <StatusPill
                                tone={line.result === 'failed' ? 'alarm' : 'neutral'}
                                size="sm"
                            >
                                {billingCopy.batchResult[line.result]}
                            </StatusPill>
                            <span className="min-w-[160px] flex-1 text-[13px] leading-[1.45] text-[var(--text-secondary)]">
                                {line.problem ?? billingCopy.batchUnknownReason}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : null}

            {retryable.length > 0 ? (
                <div className="px-4 py-[14px]">
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={retrying}
                        onClick={() => onRetry(retryable.map((line) => line.invoiceUuid))}
                    >
                        {billingCopy.batchRetry(retryable.length)}
                    </Button>
                </div>
            ) : null}
        </Card>
    );
}
